/**
 * Concurrency helpers for rule creation, shared by the Entry (opener) and
 * Unwinding pages — the two are deliberate mirrors.
 *
 * A rule-less row has several gestures that can create its rule (param
 * ✓/Enter, a maker switch, the TT switch on Entry; the Kalshi/Polymarket
 * toggles on Unwinding). Before these helpers, each gesture POSTed
 * independently: pressing one and then another ~1s later (before the rules
 * refetch landed) fired a SECOND create for the same pair, leaving a
 * permanent orphan duplicate rule. The invariant now is: at most ONE create
 * in flight per pair — every later gesture joins that create's promise and
 * applies its flag via the normal PATCH path on the resolved rule.
 */

export interface SingleFlight<T> {
  /**
   * Returns the in-flight promise for `key` if one exists; otherwise
   * starts `create()` and registers its promise under `key` until it
   * settles (success or failure — a failed create must be retryable).
   */
  ensure: (key: string, create: () => Promise<T>) => Promise<T>;
  inFlight: (key: string) => boolean;
}

export function createSingleFlight<T>(): SingleFlight<T> {
  const pending = new Map<string, Promise<T>>();
  return {
    ensure(key, create) {
      const existing = pending.get(key);
      if (existing) return existing;
      const entry: Promise<T> = create().finally(() => {
        if (pending.get(key) === entry) pending.delete(key);
      });
      pending.set(key, entry);
      return entry;
    },
    inFlight: key => pending.has(key)
  };
}

/**
 * Duplicate-create conflict reported by the backend: POST /rules answers
 * HTTP 409 with `{ message, existingRuleId }` when the pair already has a
 * rule. Returns the conflict payload, or null when the error is anything
 * else (caller rethrows).
 */
export function extractDuplicateRuleConflict(
  err: unknown
): { existingRuleId: string } | null {
  const response = (
    err as {
      response?: { status?: number; data?: { existingRuleId?: unknown } };
    } | null
  )?.response;
  if (!response || response.status !== 409) return null;
  const existingRuleId = response.data?.existingRuleId;
  if (typeof existingRuleId !== 'string' || !existingRuleId) return null;
  return { existingRuleId };
}

/**
 * 409 recovery for the create mutation: a duplicate-create conflict means
 * "the rule already exists", so resolve to that rule (via `fetchRule`)
 * instead of surfacing a failure — the caller continues with its PATCH
 * exactly as if this call had created the rule. Any other error rethrows
 * untouched.
 */
export async function recoverFromDuplicateCreate<T>(
  err: unknown,
  fetchRule: (id: string) => Promise<T>
): Promise<T> {
  const conflict = extractDuplicateRuleConflict(err);
  if (!conflict) throw err;
  return fetchRule(conflict.existingRuleId);
}

/**
 * Cache seeding for the create mutation's onSuccess: computes the updated
 * rule lists so the freshly created rule is visible the moment the create
 * resolves. Invalidation alone left a ~1-5s window where rows still saw
 * `rule === undefined` and a second gesture fired a duplicate create.
 *
 * `lists` is `queryClient.getQueriesData(...)` output: `[key, data]` pairs
 * where an array key's second element is the list's ruleType filter ('all'
 * for the unfiltered list). The rule is seeded into the 'all' list and the
 * list matching its ruleType; `defaultRuleType` supplies the type for
 * legacy docs that lack one (the unwinder defaults those to 'tna').
 * Lists that already contain the rule (or hold no data) are skipped.
 */
export function seedRuleIntoLists<
  T extends { _id: string; ruleType?: string },
  K
>(
  lists: ReadonlyArray<readonly [K, T[] | undefined]>,
  created: T,
  opts?: { defaultRuleType?: string }
): Array<[K, T[]]> {
  const createdType = created.ruleType ?? opts?.defaultRuleType;
  const updates: Array<[K, T[]]> = [];
  for (const [key, data] of lists) {
    const keyRuleType = Array.isArray(key) ? key[1] : 'all';
    if (keyRuleType !== 'all' && createdType && keyRuleType !== createdType) {
      continue;
    }
    if (!Array.isArray(data)) continue;
    if (data.some(r => r._id === created._id)) continue;
    updates.push([key, [...data, created]]);
  }
  return updates;
}

/**
 * Merge a per-row param-update DTO onto a cached rule for the OPTIMISTIC cache
 * patch behind the per-row ✓ confirm (opener `entry-rules`, unwinder
 * `unwinding-rules`). Mirror of the maker-flag optimistic patch.
 *
 * WHY: the row's Mode / Hedge / Wait / Gate / Sweep drafts re-sync off
 * the rule's COMMITTED value (keyed effects in EntryTabByPath /
 * UnwindingTabSumOfSides). With a bare `invalidateQueries` on success, a stale
 * `*-rules` poll that was already in flight when the ✓ was pressed lands AFTER
 * the write and reverts the committed field in the cache — the keyed effect
 * then snaps the operator's just-saved dropdown back to the old value even
 * though the write returned 200. The operator re-does it, producing the
 * repeated-PATCH-on-the-same-rule pattern seen in the feed-pod logs. Patching
 * the cache to the committed values (paired with `cancelQueries` +
 * `debouncedInvalidate*` in the caller) closes that window.
 *
 * Shallow-merges every top-level field the DTO carries, DEEP-merges the nested
 * `kalshiConfig` / `polymarketConfig` (so untouched sub-fields like
 * `targetTnaPercent` survive a size-only edit), and reflects the flat
 * `pathA/BThresholdSum` into the nested `pathA` / `pathB` the tables read. The
 * caller's `onSettled` refetch reconciles anything the merge can't know
 * (resolved config sides, auto-pause side-effects).
 */
export function mergeRuleDtoOptimistic<R extends { _id: string }>(
  rule: R,
  dto: object
): R {
  const {
    pathAThresholdSum,
    pathBThresholdSum,
    kalshiConfig,
    polymarketConfig,
    ...rest
  } = dto as Record<string, any>;
  const src = rule as Record<string, any>;
  const next: Record<string, any> = { ...src, ...rest };
  if (kalshiConfig && typeof kalshiConfig === 'object') {
    next.kalshiConfig = { ...(src.kalshiConfig ?? {}), ...kalshiConfig };
  }
  if (polymarketConfig && typeof polymarketConfig === 'object') {
    next.polymarketConfig = {
      ...(src.polymarketConfig ?? {}),
      ...polymarketConfig
    };
  }
  if (pathAThresholdSum != null) {
    next.pathA = { ...(src.pathA ?? {}), thresholdSum: pathAThresholdSum };
  }
  if (pathBThresholdSum != null) {
    next.pathB = { ...(src.pathB ?? {}), thresholdSum: pathBThresholdSum };
  }
  return next as R;
}

/**
 * Roll back ONE rule's optimistic patch on a failed `mergeRuleDtoOptimistic`
 * update, restoring it from the pre-mutation snapshot while leaving every OTHER
 * row untouched. Restoring the WHOLE snapshot array (the naive rollback) would
 * clobber a sibling row a concurrent confirm committed after this mutation's
 * `onMutate` snapshot was taken — re-introducing the very snap-back this file
 * fixes, just under concurrent same-list confirms. So we surgically replace
 * only the failed `id`:
 *  - present in the snapshot → restore that pre-mutation row;
 *  - absent from the snapshot (rule didn't exist yet) → drop the optimistic row.
 * Every other row keeps its CURRENT (possibly newer) cache value.
 */
export function rollbackRuleInList<R extends { _id: string }>(
  currentList: R[] | undefined,
  snapshotList: readonly R[] | undefined,
  id: string
): R[] | undefined {
  if (!currentList) return currentList;
  const original = snapshotList?.find(r => r._id === id);
  if (!original) return currentList.filter(r => r._id !== id);
  return currentList.map(r => (r._id === id ? original : r));
}
